import { useEffect, useState } from 'react';

/**
 * The STAGING marker. A second environment's biggest real-world risk is not technical — it is a human
 * confusing it for production: entering real figures into staging, or testing destructively against
 * production believing it is staging. So this is deliberately loud, and it renders BEFORE login (you must
 * know which tier you are on while you are still typing the password, not after).
 *
 * The tier comes from `/api/health` (public, unauthenticated, so the banner works on the login screen).
 * It is asked of the SERVER rather than inferred from the hostname: the hostname is a guess, the server's
 * own `APP_ENV` is the same value that decides whether مودیان submissions are real (see server.ts
 * `filesRealInvoices`). If the two ever disagree, the server is right.
 *
 * FAILS SILENT on purpose: if /api/health is unreachable or the field is missing, no banner. The absence
 * of the banner therefore never means "this is production" — only the presence means "this is staging".
 * A false STAGING banner on production would train people to ignore it, which is worse than none.
 */
export function StagingBanner(): React.JSX.Element | null {
  const [staging, setStaging] = useState(false);

  useEffect(() => {
    let alive = true;
    void fetch('/api/health')
      .then((r) => (r.ok ? r.json() : null))
      .then((b: { appEnv?: string } | null) => {
        if (!alive || b?.appEnv !== 'staging') return;
        setStaging(true);
        // The tab title is the marker that survives a screenshot, a shared link, and a crowded tab bar.
        if (!document.title.startsWith('[STAGING]')) document.title = `[STAGING] ${document.title}`;
      })
      .catch(() => {
        /* fail silent — see docstring */
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!staging) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        background:
          'repeating-linear-gradient(135deg,#7f1d1d,#7f1d1d 12px,#991b1b 12px,#991b1b 24px)',
        color: '#fff',
        textAlign: 'center',
        padding: '6px 12px',
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: 0.2,
        boxShadow: '0 1px 6px rgba(0,0,0,.35)',
      }}
    >
      محیط آزمایشی (STAGING) — داده‌های اینجا واقعی نیستند و به مؤدیان ارسال نمی‌شوند. نسخهٔ
      عملیاتی:{' '}
      <a href="https://app.kiaemp.com" style={{ color: '#fff', textDecoration: 'underline' }}>
        app.kiaemp.com
      </a>
    </div>
  );
}
